import React, { useMemo, useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFieldDependentTable } from '../../../utils/metadata/tables';
import { useGlobalMetadata } from '../../../utils/providers';
import { insertRecord, updateRecord, handleOperationError } from '../../../utils/supabase-operations';
import { useMyCapabilities } from '../../../utils/metadata/capabilities';
import { invalidateAndRefreshAllQueriesForTable } from '../../../utils/metadata';
import { requestReportGridRefresh } from '../../../utils/reportGridRefresh';
import { toast } from 'sonner';
import { useRecordNavigator } from '../../../hooks/useRecordNavigator';
import StatusPill from './atoms/StatusPill';
import StatusPillSelect from './atoms/StatusPillSelect';
import KV from './atoms/KV';
import SectionLabel from './atoms/SectionLabel';
import { formatSubmissionTerm } from '../../../utils/submissionTerm';
import { missingDocumentsList } from '../../../utils/missingDocuments';
import { merchantDealCyclesQueryOptions, merchantSubmissionsRequest } from '../../../utils/destinationQueries/merchantPanel';
import DealPrepHistoryDialog from '../../DealPrep/DealPrepHistoryDialog';

interface MerchantSubmissionsTabProps {
  merchant: Record<string, any> | null;
  merchantId: number;
  isLoading: boolean;
}

function MerchantSubmissionsTab({ merchant, merchantId, isLoading }: MerchantSubmissionsTabProps) {
  const { data: metadata } = useGlobalMetadata();
  const { openRecord } = useRecordNavigator();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  // REG-721: one-click submission history into the DEAL PREP chain. The
  // capability is enforced by the edge function; this only hides the button.
  const { hasCapability } = useMyCapabilities();
  const canSendDealPrep = hasCapability('send_deal_prep_history');
  const [dealPrepOpen, setDealPrepOpen] = useState(false);

  const subTableInfo = metadata?.tableData?.byName?.['submission'];
  const subSchema = subTableInfo?.schema_name || 'public';
  const subRouteName = (metadata?.routeData as any)?.tableToRouteName?.['submission'] || 'submission';

  const submissionsRequest = useMemo(
    () => merchantSubmissionsRequest(merchantId, subSchema),
    [merchantId, subSchema],
  );

  const { data: submissions } = useFieldDependentTable({
    enabled: !!merchantId,
    ...submissionsRequest,
  });

  // REG-649: deal-cycle grouping. submission_deep_view does not expose the
  // derived deal_cycle link (regenerating submission views is blocked by
  // dependent report views), so fetch the id -> cycle map and the merchant's
  // cycles directly; both are RLS-scoped to visible merchants. On failure the
  // list gracefully falls back to a single flat group.
  const { data: cycleData } = useQuery({
    ...merchantDealCyclesQueryOptions(merchantId),
    enabled: !!merchantId,
  });

  const subs = (Array.isArray(submissions) ? submissions : []) as Record<string, any>[];
  const m = merchant;
  const primaryApprovalId = Number(m?.primary_approval) || null;
  const cycleGroups = useMemo(
    () => groupByCycle(subs, cycleData ?? null, primaryApprovalId),
    [subs, cycleData, primaryApprovalId],
  );

  // REG-811: edit_deals users change a submission's status from its pill.
  const canEditStatus = hasCapability('edit_deals');
  const statusPicklist = (metadata as any)?.picklistData?.submission?.byFieldName?.status?.values;
  const statusOptions = useMemo<Array<{ value: string; label?: string }>>(
    () => (Array.isArray(statusPicklist) ? statusPicklist.filter((option: any) => option?.value) : []),
    [statusPicklist],
  );
  const statusDataType: string | undefined = (metadata as any)?.fieldData?.submission?.byFieldName?.status?.data_type;

  const changeStatus = useCallback(async (s: Record<string, any>, next: string) => {
    const result = await updateRecord({
      tableName: 'submission',
      schema: subSchema,
      data: { status: next },
      whereClause: { id: s.id },
      // Someone else may have moved the deal since the panel loaded.
      options: { invalidateCache: false, expectedValues: { status: s.status ?? null }, expectedValueTypes: { status: statusDataType } },
    });
    if (result.error) {
      const info = handleOperationError(result.error, 'Failed to change status');
      toast.error(info.userMessage);
      return;
    }
    // The merchant's own status and the grids behind the panel follow the deal.
    await Promise.all([
      invalidateAndRefreshAllQueriesForTable('submission'),
      invalidateAndRefreshAllQueriesForTable('merchant'),
    ]);
    requestReportGridRefresh();
    toast.success(`Status set to ${next}`);
  }, [subSchema, statusDataType]);

  const statusControl = useCallback((s: Record<string, any>) => (
    canEditStatus && statusOptions.length > 0
      ? { options: statusOptions, onChange: (next: string) => changeStatus(s, next) }
      : null
  ), [canEditStatus, statusOptions, changeStatus]);

  // The Overview pill drives the deal at the top of the newest cycle: the
  // primary approval, else the most advanced submission (same order as the list).
  const leadSubmission = cycleGroups[0]?.subs[0] ?? null;

  const [showNewForm, setShowNewForm] = useState(false);
  const [newAmount, setNewAmount] = useState('');

  const handleCreateSubmission = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    try {
      const insertData: Record<string, any> = { merchant: merchantId };
      if (newAmount) insertData.amount = Number(newAmount);
      // funder is FK (number) — if user typed a name we skip it, only set if we had a proper value
      // For now just insert with merchant + amount, user fills rest in detail view

      const { data, error } = await insertRecord({
        tableName: 'submission',
        schema: subSchema,
        data: insertData,
      });
      if (error) {
        const info = handleOperationError(error, 'Failed to create submission');
        toast.error(info.userMessage);
        return;
      }
      const newRec = Array.isArray(data) ? data[0] : data;
      if (newRec?.id) {
        await invalidateAndRefreshAllQueriesForTable('submission');
        // The Submissions list and its submenus are report grids (REG-828).
        requestReportGridRefresh();
        setShowNewForm(false);
        setNewAmount('');
        openRecord({
          id: newRec.id,
          displayId: newRec.submission_number || newRec.id,
          table: subRouteName,
          tableName: 'submission',
          label: `Submission #${newRec.submission_number || newRec.id}`,
        });
      }
    } catch (error) {
      const info = handleOperationError(error, 'Failed to create submission');
      toast.error(info.userMessage);
    } finally {
      setCreating(false);
    }
  }, [creating, subSchema, merchantId, newAmount, queryClient, openRecord, subRouteName]);

  const handleCancelNew = useCallback(() => {
    setShowNewForm(false);
    setNewAmount('');
  }, []);

  const handleOpenSubmission = useCallback((s: Record<string, any>) => {
    const lender = relName(s.funder_rel) || s.funder || '';
    openRecord({
      id: s.id,
      displayId: s.submission_number || s.id,
      table: subRouteName,
      tableName: 'submission',
      label: lender ? `${lender} #${s.submission_number || s.id}` : `Submission #${s.submission_number || s.id}`,
    });
  }, [openRecord, subRouteName]);

  if (isLoading) {
    return <div className="sky-mp-empty">Loading...</div>;
  }

  if (!m) {
    return <div className="sky-mp-empty">No merchant data found.</div>;
  }

  return (
    <>
      <SummaryCard m={m} leadSubmission={leadSubmission} statusControl={leadSubmission ? statusControl(leadSubmission) : null} />
      <SectionLabel
        right={
          <span className="sky-mp-section-actions">
            {canSendDealPrep && subs.length > 0 && (
              <button
                type="button"
                className="sky-mp-new-btn sky-mp-deal-prep-btn"
                data-testid="deal-prep-history-btn"
                title="Email this merchant's submission history into the DEAL PREP chain"
                onClick={() => setDealPrepOpen(true)}
              >
                Send history to DEAL PREP
              </button>
            )}
            {!showNewForm ? (
              <button
                type="button"
                className="sky-mp-new-btn"
                onClick={() => setShowNewForm(true)}
              >
                + New
              </button>
            ) : null}
          </span>
        }
      >
        Submissions {subs.length > 0 && `\u00B7 ${subs.length}`}
      </SectionLabel>

      {canSendDealPrep && (
        <DealPrepHistoryDialog
          merchantId={merchantId}
          isOpen={dealPrepOpen}
          onClose={() => setDealPrepOpen(false)}
        />
      )}

      {showNewForm && (
        <div className="sky-mp-new-form">
          <div className="sky-mp-new-form-row">
            <label className="sky-mp-new-form-label">
              Amount
              <input
                type="number"
                className="sky-mp-new-form-input"
                placeholder="e.g. 50000"
                value={newAmount}
                onChange={e => setNewAmount(e.target.value)}
                autoFocus
              />
            </label>
          </div>
          <div className="sky-mp-new-form-actions">
            <button
              type="button"
              className="sky-mp-new-form-save"
              onClick={handleCreateSubmission}
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create Submission'}
            </button>
            <button
              type="button"
              className="sky-mp-new-form-cancel"
              onClick={handleCancelNew}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="sky-mp-sub-list">
        {cycleGroups.map((group) => (
          <React.Fragment key={group.key}>
            {group.label && <div className="sky-mp-cycle-divider">{group.label}</div>}
            {group.subs.map((s: any) => (
              <SubmissionCard
                key={s.id}
                s={s}
                isPrimary={primaryApprovalId != null && s.id === primaryApprovalId}
                onClick={() => handleOpenSubmission(s)}
                statusControl={statusControl(s)}
              />
            ))}
          </React.Fragment>
        ))}
        {subs.length === 0 && (
          <div className="sky-mp-empty">No submissions yet.</div>
        )}
      </div>
    </>
  );
}

/* ── Deal-cycle grouping (REG-649) ──────────────────────── */

interface CycleGroup {
  key: string;
  /** Divider label; omitted when there is only one flat group. */
  label: string | null;
  subs: Record<string, any>[];
}

/**
 * Relevance band within a cycle. The history data motivates this: 74% of
 * submissions are declines and 40% of approval cycles carry competing offers,
 * so approvals surface first and declines sink.
 */
function statusRank(status: string): number {
  const lower = (status || '').toLowerCase();
  if (lower.includes('funded')) return 1;
  if (lower.includes('contracts in')) return 2;
  if (lower.includes('contract')) return 3;
  if (lower.includes('approved')) return 4;
  if (lower.includes('missing') || lower.includes('await')) return 5;
  if (lower.includes('waiting')) return 6;
  if (lower.includes('no response')) return 8;
  if (lower.includes('declined') || lower.includes('dead')) return 9;
  return 7; // Submitted / new / unknown: between waiting and no-response.
}

function cardRank(s: Record<string, any>, primaryApprovalId: number | null): number {
  if (primaryApprovalId != null && s.id === primaryApprovalId) return 0;
  return statusRank(s.status);
}

function activityTime(s: Record<string, any>): number {
  const value = s.submission_date || s.status_change_date || s.created_at;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
}

function cycleLabel(
  cycle: { cycle_sequence: number; close_reason: string | null; closed_at: string | null } | undefined,
  cycleId: number | null,
): string {
  if (cycleId == null || !cycle) return 'Earlier activity';
  const sequence = `Deal Cycle ${cycle.cycle_sequence}`;
  const closedOn = cycle.closed_at ? new Date(cycle.closed_at).toLocaleDateString() : '';
  if (cycle.close_reason === 'funded') return `${sequence} · funded${closedOn ? ` ${closedOn}` : ''}`;
  if (cycle.close_reason) return `${sequence} · closed${closedOn ? ` ${closedOn}` : ''}`;
  return `${sequence} · current`;
}

/**
 * Newest cycle always above older ones; unassigned history sinks to the
 * bottom. Within a cycle: primary approval first, then by status band, then
 * highest amount, then latest activity.
 */
function groupByCycle(
  subs: Record<string, any>[],
  cycleData: {
    cycleBySubmission: Map<number, number | null>;
    cycleById: Map<number, { id: number; cycle_sequence: number; close_reason: string | null; closed_at: string | null }>;
  } | null,
  primaryApprovalId: number | null,
): CycleGroup[] {
  const sortWithinCycle = (a: Record<string, any>, b: Record<string, any>) =>
    cardRank(a, primaryApprovalId) - cardRank(b, primaryApprovalId)
    || (Number(b.amount) || 0) - (Number(a.amount) || 0)
    || activityTime(b) - activityTime(a);

  if (!cycleData || cycleData.cycleById.size === 0) {
    return subs.length > 0
      ? [{ key: 'all', label: null, subs: [...subs].sort(sortWithinCycle) }]
      : [];
  }

  const byCycle = new Map<number | null, Record<string, any>[]>();
  for (const s of subs) {
    const cycleId = cycleData.cycleBySubmission.get(s.id) ?? null;
    const bucket = byCycle.get(cycleId) ?? [];
    bucket.push(s);
    byCycle.set(cycleId, bucket);
  }

  return [...byCycle.entries()]
    .sort(([a], [b]) => {
      if (a == null) return 1;
      if (b == null) return -1;
      const seqA = cycleData.cycleById.get(a)?.cycle_sequence ?? 0;
      const seqB = cycleData.cycleById.get(b)?.cycle_sequence ?? 0;
      return seqB - seqA;
    })
    .map(([cycleId, bucket]) => ({
      key: cycleId == null ? 'unassigned' : `cycle-${cycleId}`,
      label: cycleLabel(cycleId == null ? undefined : cycleData.cycleById.get(cycleId), cycleId),
      subs: bucket.sort(sortWithinCycle),
    }));
}

/* ── Summary / Overview card ────────────────────────────── */

interface StatusControl {
  options: Array<{ value: string; label?: string }>;
  onChange: (next: string) => Promise<void>;
}

function SummaryCard({ m, leadSubmission, statusControl }: {
  m: Record<string, any>;
  leadSubmission: Record<string, any> | null;
  statusControl: StatusControl | null;
}) {
  // Zack's 8/28 rule: the merchant-level status reflects the most recent
  // deal cycle (deal_status_calc, derived by public.merchant_deal_status —
  // e.g. an approved primary shows Approved, an ever-funded merchant stays
  // Funded). Raw status remains the fallback for older cached rows.
  const status = m.deal_status_calc || m.status_rel_name || m.status || '';
  const appDate = m.created_at ? new Date(m.created_at).toLocaleDateString() : '';

  return (
    <div className="sky-mp-summary">
      <div className="sky-mp-summary-head">
        <span className="sky-mp-summary-label">OVERVIEW</span>
        {status && statusControl && leadSubmission ? (
          <StatusPillSelect
            pillStatus={mapStatus(status)}
            label={`${status}${appDate ? ` \u2022 ${appDate}` : ''}`}
            value={leadSubmission.status || ''}
            options={statusControl.options}
            onChange={statusControl.onChange}
            title={`Change status of ${relName(leadSubmission.funder_rel) || 'submission'} #${leadSubmission.id}`}
          />
        ) : status && (
          <StatusPill status={mapStatus(status)} label={`${status}${appDate ? ` \u2022 ${appDate}` : ''}`} />
        )}
      </div>
      {/* Group 1: Identity & Contact */}
      <div className="sky-mp-summary-grid">
        <KV k="Merchant Name" v={properCase(m.full_name_calc || `${m.first_name || ''} ${m.last_name || ''}`.trim())} />
        <KV k="Status" v={status} />
        <KV k="Phone" v={m.phone} copy />
        <KV k="Email" v={m.email} copy />
      </div>
      {/* Group 2: Deal Info — column names follow merchant_deep_view
          (`requesting`, `merchant_response`), the same fields the Basic Info
          tab edits. */}
      <div className="sky-mp-summary-grid sky-mp-summary-grid--sep">
        <KV k="Requesting" v={formatMoney(m.requesting)} />
        <KV k="Merchant Feedback" v={m.merchant_response} />
        <KV k="Revenue" v={formatMoney(m.monthly_revenue)} />
        <KV k="Credit" v={m.credit_score} />
      </div>
      {/* Group 3: Business Details */}
      <div className="sky-mp-summary-grid sky-mp-summary-grid--sep">
        <KV k="Industry" v={relName(m.industry_rel) || m.industry} />
        <KV k="Business Start Date" v={m.date_business_started ? new Date(m.date_business_started).toLocaleDateString() : ''} />
        <KV k="Lead Source" v={relName(m.lead_source_rel) || m.lead_source} />
        <KV k="Marketing Brand" v={m.MarketingBrandDB} />
      </div>
    </div>
  );
}

/* ── Submission card ────────────────────────────────────── */

function SubmissionCard({ s, onClick, isPrimary = false, statusControl = null }: {
  s: Record<string, any>;
  onClick?: () => void;
  isPrimary?: boolean;
  statusControl?: StatusControl | null;
}) {
  const status = s.status || '';
  const statusKey = mapStatus(status);
  const isDeclined = statusKey === 'declined';
  const isMissing = statusKey === 'missing';
  const lender = relName(s.funder_rel) || s.funder || '';
  const date = (s.submission_date || s.created_at)
    ? new Date(s.submission_date || s.created_at).toLocaleDateString()
    : '';
  const subId = s.id ? `#${s.id}` : '';
  // "Awaiting Docs" / "Waiting On" status spellings mean the same thing as
  // Missing Documents — the outstanding-docs list is the live detail for all
  // of them, even though they map to a different status pill.
  const awaitingDocs = /await|waiting/.test((status || '').toLowerCase());
  const declineDetail = isDeclined ? issueDetail(s.reason_for_decline) : '';
  const missingDetail = (isMissing || awaitingDocs) ? issueDetail(s.missing_documents) : '';

  const showDetails = !isDeclined && !isMissing;
  const details = showDetails ? [
    formatMoney(s.amount),
    s.factor_rate ? String(s.factor_rate) : null,
    formatSubmissionTerm(s.term, relName(s.structure_rel) || s.structure) || null,
    formatMoney(s.payment_calc),
    s.commission_percent ? `${s.commission_percent}%` : null,
  ].filter(Boolean) : null;

  return (
    <div className="sky-mp-sub-card" onClick={onClick} style={{ cursor: onClick ? 'pointer' : undefined }}>
      <div className="sky-mp-sub-card-top">
        <div className="sky-mp-sub-card-info">
          <span className="sky-mp-sub-date">{date}</span>
          <span className="sky-mp-sub-lender">{lender}</span>
          <span className="sky-mp-sub-id">{subId}</span>
        </div>
        <span className="sky-mp-sub-pills">
          {isPrimary && <span className="sky-mp-sub-primary" title="This merchant's canonical primary approval">PRIMARY</span>}
          {statusControl ? (
            <StatusPillSelect
              pillStatus={statusKey}
              label={statusLabel(status)}
              value={status}
              options={statusControl.options}
              onChange={statusControl.onChange}
              title={`Change status of ${lender || 'submission'} ${subId}`}
            />
          ) : (
            <StatusPill status={statusKey} label={statusLabel(status)} />
          )}
        </span>
      </div>
      {details && details.length > 0 && (
        <div className="sky-mp-sub-details">
          {details.map((d, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="sky-mp-sub-dot">&bull;</span>}
              <span>{d}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      {/* The status pill above already says "Declined" — only the reason adds
          anything, which is how the missing-documents case below reads too. */}
      {isDeclined && declineDetail && (
        <div className="sky-mp-sub-decline">{declineDetail}</div>
      )}
      {missingDetail && (
        <div className="sky-mp-sub-missing">{missingDetail}</div>
      )}
    </div>
  );
}

/* ── Helpers ────────────────────────────────────────────── */


/** CINDY YANEZ / tim raper → Cindy Yanez / Tim Raper. */
function properCase(value: string): string {
  return value.replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

/** Extract display name from a _rel JSON column (e.g. funder_rel) */
function relName(rel: any): string {
  if (!rel) return '';
  if (typeof rel === 'string') return rel;
  // rel is typically { id, name/display_name/... }
  return rel.display_name || rel.name || rel.full_name || rel.dba_name || '';
}

/** Most values are plain text; migrated missing-document values can be JSON arrays. */
function issueDetail(value: any): string {
  return missingDocumentsList(value);
}

function mapStatus(s: string): string {
  const lower = (s || '').toLowerCase();
  if (lower.includes('approved')) return 'approved';
  if (lower.includes('funded')) return 'funded';
  if (lower.includes('declined') || lower.includes('dead')) return 'declined';
  if (lower.includes('missing')) return 'missing';
  if (lower.includes('contract')) return 'contracts';
  if (lower.includes('submitted') || lower.includes('new')) return 'submitted';
  return '';
}

function statusLabel(s: string): string {
  const lower = (s || '').toLowerCase();
  if (lower.includes('approved')) return 'Approved';
  if (lower.includes('funded')) return 'Funded';
  if (lower.includes('declined')) return 'Declined';
  if (lower.includes('missing')) return 'Missing Doc';
  // Contracts In and Contracts Out share a pill colour but are different stages.
  if (lower.includes('contracts in')) return 'Contracts In';
  if (lower.includes('contract')) return 'Contracts Out';
  return s;
}

function formatMoney(v: any): string {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return '$' + n.toLocaleString('en-US');
}

export default React.memo(MerchantSubmissionsTab);
